import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import jsPDF from 'jspdf';
import './SchoolDashboard.css';

function Billing() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();
  
  // Selectors
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [terms, setTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState('');
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [viewMode, setViewMode] = useState('all'); // 'all', 'class-wise', 'single'
  
  // Data
  const [students, setStudents] = useState([]);
  const [billingData, setBillingData] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentInvoices, setStudentInvoices] = useState({}); // Map of student_id -> invoice
  
  // Invoice generation
  const [showInvoicePopup, setShowInvoicePopup] = useState(false);
  const [invoiceMode, setInvoiceMode] = useState(''); // 'single', 'class', 'all'
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [showInvoiceSuccessPopup, setShowInvoiceSuccessPopup] = useState(false);
  const [invoiceSuccessData, setInvoiceSuccessData] = useState(null);

  // Helper function to get base class name without section
  const getClassDisplayName = (className) => {
    if (!className) return 'No Class';
    if (className.includes(' - ')) {
      return className.split(' - ')[0].trim();
    }
    return className.trim();
  };

  useEffect(() => {
    const storedSchoolData = localStorage.getItem('schoolData');
    if (!storedSchoolData) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(storedSchoolData));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchAcademicYears();
      fetchClasses();
      fetchTerms();
    }
  }, [schoolData?.id, selectedBranchId]);

  useEffect(() => {
    if (schoolData?.id && selectedAcademicYear) {
      fetchBillingData();
    }
  }, [schoolData?.id, selectedBranchId, selectedAcademicYear, selectedTerm, selectedClass, viewMode]);

  const fetchAcademicYears = async () => {
    try {
      const { data, error } = await supabase
        .from('academic_years')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('start_date', { ascending: false });

      if (error) {
        console.error('Error fetching academic years:', error);
        return;
      }

      setAcademicYears(data || []);
      if (data && data.length > 0 && !selectedAcademicYear) {
        setSelectedAcademicYear(data[0].id);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchClasses = async () => {
    try {
      let query = supabase
        .from('classes')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('class_name', { ascending: true });

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching classes:', error);
        return;
      }

      setClasses(data || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchTerms = async () => {
    // Terms are typically: Term 1, Term 2, Term 3, etc.
    // You can customize this based on your system
    const termOptions = [
      { id: 'term1', name: 'Term 1' },
      { id: 'term2', name: 'Term 2' },
      { id: 'term3', name: 'Term 3' }
    ];
    setTerms(termOptions);
    if (termOptions.length > 0 && !selectedTerm) {
      setSelectedTerm(termOptions[0].id);
    }
  };

  const fetchBillingData = async () => {
    try {
      setLoadingData(true);
      
      // Fetch students based on view mode
      let studentsQuery = supabase
        .from('students')
        .select(`
          id,
          first_name,
          middle_name,
          last_name,
          student_id_no,
          class_id,
          admission_date,
          classes:class_id (id, class_name)
        `)
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId) {
        studentsQuery = studentsQuery.eq('branch_id', selectedBranchId);
      } else {
        studentsQuery = studentsQuery.is('branch_id', null);
      }

      if (viewMode === 'class-wise' && selectedClass) {
        studentsQuery = studentsQuery.eq('class_id', selectedClass);
      }

      const { data: studentsData, error: studentsError } = await studentsQuery.order('first_name');

      if (studentsError) {
        console.error('Error fetching students:', studentsError);
        return;
      }

      setStudents(studentsData || []);

      // Fetch class fee mappings for the selected academic year
      if (studentsData && studentsData.length > 0 && selectedAcademicYear) {
        // Get unique class IDs from students
        const classIds = [...new Set(studentsData.map(s => s.class_id).filter(Boolean))];
        
        if (classIds.length === 0) {
          setBillingData([]);
          return;
        }

        // Fetch class fee mappings (no term filter - class_fee_mapping doesn't have term field)
        let mappingsQuery = supabase
          .from('class_fee_mapping')
          .select('*')
          .eq('school_id', schoolData.id)
          .eq('academic_year_id', selectedAcademicYear)
          .eq('status', 'published')
          .in('class_id', classIds);

        // For branch filtering: if branch is selected, get mappings for that branch OR null branch
        // If no branch selected, get mappings with null branch
        if (selectedBranchId) {
          // Get mappings for the selected branch OR null branch (school-wide mappings)
          // Use .or() with proper syntax: field.eq.value,field.is.null
          mappingsQuery = mappingsQuery.or(`branch_id.eq.${selectedBranchId},branch_id.is.null`);
        } else {
          mappingsQuery = mappingsQuery.is('branch_id', null);
        }

        const { data: feeMappings, error: mappingsError } = await mappingsQuery;

        if (mappingsError) {
          console.error('Error fetching fee mappings:', mappingsError);
          setBillingData([]);
          return;
        }

        console.log('Fetched fee mappings:', feeMappings);
        console.log('Students:', studentsData);
        console.log('Class IDs:', classIds);

        // Generate billing data from mappings for each student
        const generatedBillingData = [];
        
        studentsData.forEach(student => {
          if (!student.class_id) return;

          // Find mappings for this student's class (ensure class_id matches)
          const classMappings = (feeMappings || []).filter(mapping => 
            parseInt(mapping.class_id) === parseInt(student.class_id)
          );

          console.log(`Student ${student.first_name} ${student.last_name} (Class ID: ${student.class_id}) - Found ${classMappings.length} mappings`);

          classMappings.forEach(mapping => {
            // Check if mapping applies to this student
            let appliesToStudent = false;
            
            if (mapping.applies_to === 'All') {
              appliesToStudent = true;
            } else if (mapping.applies_to === 'New') {
              // Check if student is new based on admission_date
              // A student is "new" if they were admitted in the current academic year
              if (student.admission_date) {
                const admissionDate = new Date(student.admission_date);
                const academicYear = academicYears.find(ay => ay.id === parseInt(selectedAcademicYear));
                if (academicYear && academicYear.start_date) {
                  const yearStart = new Date(academicYear.start_date);
                  appliesToStudent = admissionDate >= yearStart;
                } else {
                  appliesToStudent = true; // Default to true if we can't determine
                }
              } else {
                appliesToStudent = true; // Default to true if no admission date
              }
            } else if (mapping.applies_to === 'Returning') {
              // Check if student is returning (not new)
              if (student.admission_date) {
                const admissionDate = new Date(student.admission_date);
                const academicYear = academicYears.find(ay => ay.id === parseInt(selectedAcademicYear));
                if (academicYear && academicYear.start_date) {
                  const yearStart = new Date(academicYear.start_date);
                  appliesToStudent = admissionDate < yearStart;
                } else {
                  appliesToStudent = true; // Default to true if we can't determine
                }
              } else {
                appliesToStudent = true; // Default to true if no admission date
              }
            }

            if (appliesToStudent) {
              // Check if billing record already exists
              const existingBilling = generatedBillingData.find(
                b => b.student_id === student.id && 
                     b.class_fee_mapping_id === mapping.id
              );

              if (!existingBilling) {
                // Create billing data object from mapping
                generatedBillingData.push({
                  id: `temp-${student.id}-${mapping.id}`,
                  student_id: student.id,
                  class_fee_mapping_id: mapping.id,
                  academic_year_id: mapping.academic_year_id,
                  term: selectedTerm || 'N/A', // Use selected term from UI
                  fee_name: mapping.fee_name,
                  fee_category: mapping.fee_category,
                  amount: parseFloat(mapping.amount) || 0,
                  discount: 0, // Default discount, can be updated later
                  balance: parseFloat(mapping.amount) || 0,
                  status: 'pending',
                  due_date: mapping.payment_due_date,
                  class_fee_mapping: {
                    fee_name: mapping.fee_name,
                    fee_category: mapping.fee_category,
                    amount: mapping.amount
                  }
                });
              }
            }
          });
        });

        console.log('Generated billing data:', generatedBillingData);
        setBillingData(generatedBillingData);

        // Fetch existing invoices for these students
        if (studentsData && studentsData.length > 0 && selectedAcademicYear && selectedTerm) {
          const studentIds = studentsData.map(s => s.id);
          const termName = terms.find(t => t.id === parseInt(selectedTerm))?.name || selectedTerm;
          
          let invoicesQuery = supabase
            .from('student_invoices')
            .select('*')
            .eq('school_id', schoolData.id)
            .eq('academic_year_id', selectedAcademicYear)
            .eq('term', termName)
            .in('student_id', studentIds);

          if (selectedBranchId) {
            invoicesQuery = invoicesQuery.eq('branch_id', selectedBranchId);
          } else {
            invoicesQuery = invoicesQuery.is('branch_id', null);
          }

          const { data: existingInvoices } = await invoicesQuery;

          // Create a map of student_id -> invoice
          const invoiceMap = {};
          if (existingInvoices) {
            existingInvoices.forEach(invoice => {
              invoiceMap[invoice.student_id] = invoice;
            });
          }

          setStudentInvoices(invoiceMap);
        }
      } else {
        setBillingData([]);
        setStudentInvoices({});
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const handleGenerateInvoice = (mode, studentId = null) => {
    setInvoiceMode(mode);
    
    if (mode === 'single' && studentId) {
      setSelectedStudents([studentId]);
    } else if (mode === 'class' && selectedClass) {
      const classStudentIds = students
        .filter(s => s.class_id === parseInt(selectedClass))
        .map(s => s.id);
      setSelectedStudents(classStudentIds);
    } else if (mode === 'all') {
      const allStudentIds = students.map(s => s.id);
      setSelectedStudents(allStudentIds);
    }
    
    setShowInvoicePopup(true);
  };

  // Helper function to format numbers with commas
  const formatNumber = (num) => {
    return parseFloat(num || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const generateInvoicePDF = (student, invoice, studentBilling, academicYear, term) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = margin;

    // Helper function to add a new page if needed
    const checkPageBreak = (requiredSpace = 20) => {
      if (yPos + requiredSpace > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage();
        yPos = margin;
        return true;
      }
      return false;
    };

    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    // School Information
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(schoolData.school_name || 'School Name', margin, yPos);
    yPos += 6;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    if (schoolData.address) {
      doc.text(schoolData.address, margin, yPos);
      yPos += 5;
    }
    if (schoolData.phone) {
      doc.text(`Phone: ${schoolData.phone}`, margin, yPos);
      yPos += 5;
    }
    if (schoolData.email) {
      doc.text(`Email: ${schoolData.email}`, margin, yPos);
      yPos += 5;
    }
    yPos += 5;

    // Invoice Details
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`Invoice Number: ${invoice.invoice_number}`, pageWidth - margin, margin + 5, { align: 'right' });
    doc.text(`Date: ${new Date().toLocaleDateString()}`, pageWidth - margin, margin + 10, { align: 'right' });
    
    if (academicYear) {
      doc.text(`Academic Year: ${academicYear.year_name || 'N/A'}`, pageWidth - margin, margin + 15, { align: 'right' });
    }
    if (term) {
      const termName = terms.find(t => t.id === term)?.name || term;
      doc.text(`Term: ${termName}`, pageWidth - margin, margin + 20, { align: 'right' });
    }
    yPos = margin + 30;

    // Student Information
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Bill To:', margin, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const studentName = `${student.first_name || ''} ${student.middle_name || ''} ${student.last_name || ''}`.trim();
    doc.text(`Student Name: ${studentName}`, margin, yPos);
    yPos += 6;
    
    if (student.student_id_no) {
      doc.text(`Admission No: ${student.student_id_no}`, margin, yPos);
      yPos += 6;
    }
    
    if (student.classes?.class_name) {
      doc.text(`Class: ${getClassDisplayName(student.classes.class_name)}`, margin, yPos);
      yPos += 6;
    }
    yPos += 5;

    // Line
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    // Table Header
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Fee Name', margin, yPos);
    doc.text('Amount', pageWidth - margin - 60, yPos, { align: 'right' });
    doc.text('Discount', pageWidth - margin - 30, yPos, { align: 'right' });
    doc.text('Balance', pageWidth - margin, yPos, { align: 'right' });
    yPos += 5;

    // Line under header
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 6;

    // Table Rows
    doc.setFont('helvetica', 'normal');
    studentBilling.forEach((bill, index) => {
      checkPageBreak(15);
      
      // Handle both billing data format and invoice_items format
      const feeName = bill.class_fee_mapping?.fee_name || bill.fee_name || 'N/A';
      const amount = parseFloat(bill.amount || 0);
      const discount = parseFloat(bill.discount || 0);
      const balance = bill.balance !== undefined ? parseFloat(bill.balance || 0) : (amount - discount);

      doc.text(feeName, margin, yPos);
      doc.text(`Rs ${formatNumber(amount)}`, pageWidth - margin - 60, yPos, { align: 'right' });
      doc.text(`Rs ${formatNumber(discount)}`, pageWidth - margin - 30, yPos, { align: 'right' });
      doc.text(`Rs ${formatNumber(balance)}`, pageWidth - margin, yPos, { align: 'right' });
      yPos += 8;
    });

    yPos += 5;
    checkPageBreak(20);

    // Totals
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Total Amount:', pageWidth - margin - 60, yPos, { align: 'right' });
    doc.text(`Rs ${formatNumber(invoice.total_amount)}`, pageWidth - margin, yPos, { align: 'right' });
    yPos += 8;

    doc.text('Total Discount:', pageWidth - margin - 60, yPos, { align: 'right' });
    doc.text(`Rs ${formatNumber(invoice.total_discount)}`, pageWidth - margin, yPos, { align: 'right' });
    yPos += 8;

    doc.setFontSize(12);
    doc.text('Total Balance:', pageWidth - margin - 60, yPos, { align: 'right' });
    doc.text(`Rs ${formatNumber(invoice.total_balance)}`, pageWidth - margin, yPos, { align: 'right' });
    yPos += 10;

    // Footer
    checkPageBreak(15);
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.text('Thank you for your payment!', pageWidth / 2, yPos, { align: 'center' });
    yPos += 5;
    doc.text('This is a computer-generated invoice.', pageWidth / 2, yPos, { align: 'center' });

    // Save PDF
    const fileName = `Invoice_${invoice.invoice_number}_${studentName.replace(/\s+/g, '_')}.pdf`;
    doc.save(fileName);
  };

  const handleConfirmInvoiceGeneration = async () => {
    if (selectedStudents.length === 0) {
      alert('No students selected for invoice generation');
      return;
    }

    setGeneratingInvoice(true);
    try {
      // Get academic year and term details
      const academicYear = academicYears.find(ay => ay.id === parseInt(selectedAcademicYear));
      const termData = terms.find(t => t.id === parseInt(selectedTerm));

      // Generate invoices for selected students
      const invoices = [];
      
      for (const studentId of selectedStudents) {
        const student = students.find(s => s.id === studentId);
        if (!student) continue;

        // Get billing data for this student
        const studentBilling = billingData.filter(b => b.student_id === studentId);
        
        if (studentBilling.length === 0) continue;

        // Calculate totals
        let totalAmount = 0;
        let totalDiscount = 0;
        let totalBalance = 0;

        studentBilling.forEach(bill => {
          const amount = parseFloat(bill.amount || 0);
          const discount = parseFloat(bill.discount || 0);
          totalAmount += amount;
          totalDiscount += discount;
          totalBalance += (amount - discount);
        });

        // Create invoice
        const termName = termData?.name || selectedTerm || 'N/A';
        const invoiceData = {
          school_id: schoolData.id,
          branch_id: selectedBranchId || null,
          academic_year_id: selectedAcademicYear,
          term: termName,
          student_id: studentId,
          invoice_number: `INV-${Date.now()}-${studentId}`,
          total_amount: totalAmount,
          total_discount: totalDiscount,
          total_balance: totalBalance,
          status: 'generated',
          due_date: studentBilling[0]?.due_date || null,
          generated_at: new Date().toISOString()
        };

        const { data: invoice, error: invoiceError } = await supabase
          .from('student_invoices')
          .insert(invoiceData)
          .select()
          .single();

        if (invoiceError) {
          console.error(`Error creating invoice for student ${studentId}:`, invoiceError);
          continue;
        }

        // Link billing items to invoice
        if (invoice && studentBilling.length > 0) {
          // Fetch class_fee_mapping details for fee names (as backup)
          const classFeeMappingIds = studentBilling
            .map(bill => bill.class_fee_mapping_id)
            .filter(Boolean);
          
          let feeMappingsData = {};
          if (classFeeMappingIds.length > 0) {
            const { data: mappingsData, error: mappingError } = await supabase
              .from('class_fee_mapping')
              .select('id, fee_name, fee_category')
              .in('id', classFeeMappingIds);
            
            if (mappingError) {
              console.error('Error fetching fee mappings:', mappingError);
            } else if (mappingsData) {
              mappingsData.forEach(mapping => {
                feeMappingsData[mapping.id] = mapping;
              });
              console.log('Fetched fee mappings:', feeMappingsData);
            }
          }

          const invoiceItems = studentBilling.map(bill => {
            // Get fee name - Priority: 1. bill.fee_name (already set from class_fee_mapping), 2. feeMappingsData, 3. bill.class_fee_mapping.fee_name
            let feeName = 'N/A';
            
            // First try bill.fee_name (this should be set from the generated billing data)
            if (bill.fee_name && bill.fee_name !== 'N/A') {
              feeName = bill.fee_name;
            } else if (bill.class_fee_mapping_id && feeMappingsData[bill.class_fee_mapping_id]) {
              feeName = feeMappingsData[bill.class_fee_mapping_id].fee_name;
            } else if (bill.class_fee_mapping?.fee_name) {
              feeName = bill.class_fee_mapping.fee_name;
            }

            console.log('Creating invoice item:', {
              billId: bill.id,
              class_fee_mapping_id: bill.class_fee_mapping_id,
              feeName: feeName,
              billFeeName: bill.fee_name,
              billClassFeeMapping: bill.class_fee_mapping,
              billData: bill
            });

            if (!feeName || feeName === 'N/A') {
              console.error('WARNING: No valid fee_name found for bill:', bill);
            }

            return {
              invoice_id: invoice.id,
              billing_id: null, // billing_id is optional, set to null since billing data is generated on-the-fly
              fee_name: feeName || 'N/A',
              term: termName,
              amount: parseFloat(bill.amount || 0),
              discount: parseFloat(bill.discount || 0),
              balance: parseFloat(bill.amount || 0) - parseFloat(bill.discount || 0)
            };
          });

          console.log('Inserting invoice items:', invoiceItems);
          console.log('Invoice ID:', invoice.id, 'Type:', typeof invoice.id);
          console.log('Number of items to insert:', invoiceItems.length);
          
          if (invoiceItems.length === 0) {
            console.warn('No invoice items to insert for invoice:', invoice.id);
          } else {
            // Validate invoice items before inserting
            const validItems = invoiceItems.filter(item => {
              if (!item.fee_name || item.fee_name === 'N/A') {
                console.warn('Skipping item with invalid fee_name:', item);
                return false;
              }
              return true;
            });

            if (validItems.length === 0) {
              console.error('No valid invoice items to insert after validation');
              alert(`Warning: No valid fee items found for invoice ${invoice.invoice_number}. Please check Class Fee Mapping.`);
            } else {
              const { data: insertedItems, error: itemsError } = await supabase
                .from('invoice_items')
                .insert(validItems)
                .select();

              if (itemsError) {
                console.error('Error inserting invoice items:', itemsError);
                console.error('Invoice items that failed:', validItems);
                console.error('Invoice ID that failed:', invoice.id);
                alert(`Failed to create invoice items for invoice ${invoice.invoice_number}. Error: ${itemsError.message}`);
              } else {
                console.log('Successfully inserted invoice items:', insertedItems);
                console.log('Number of items inserted:', insertedItems?.length || 0);
              }
            }
          }
        }

        invoices.push({ invoice, student, studentBilling });

        // Generate and download PDF for this invoice
        try {
          generateInvoicePDF(student, invoice, studentBilling, academicYear, termData);
        } catch (pdfError) {
          console.error('Error generating PDF:', pdfError);
          // Continue even if PDF generation fails
        }
      }

      setShowInvoicePopup(false);
      setSelectedStudents([]);
      
      // Update studentInvoices state with newly generated invoices
      const updatedInvoiceMap = { ...studentInvoices };
      invoices.forEach(({ invoice, student }) => {
        updatedInvoiceMap[student.id] = invoice;
      });
      setStudentInvoices(updatedInvoiceMap);
      
      // Show beautiful success popup
      setInvoiceSuccessData({
        count: invoices.length,
        invoices: invoices.map(inv => ({
          invoiceNumber: inv.invoice.invoice_number,
          studentName: `${inv.student.first_name || ''} ${inv.student.last_name || ''}`.trim()
        }))
      });
      setShowInvoiceSuccessPopup(true);
      
      await fetchBillingData();
    } catch (error) {
      console.error('Error generating invoices:', error);
      alert('Failed to generate invoices. Please try again.');
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const getStudentBillingData = (studentId) => {
    return billingData.filter(b => b.student_id === studentId);
  };

  const calculateStudentTotals = (studentId) => {
    const studentBills = getStudentBillingData(studentId);
    let totalAmount = 0;
    let totalDiscount = 0;
    let totalBalance = 0;

    studentBills.forEach(bill => {
      const amount = parseFloat(bill.amount || 0);
      const discount = parseFloat(bill.discount || 0);
      totalAmount += amount;
      totalDiscount += discount;
      totalBalance += (amount - discount);
    });

    return { totalAmount, totalDiscount, totalBalance };
  };

  const downloadInvoicePDF = async (studentId) => {
    const invoice = studentInvoices[studentId];
    if (!invoice) {
      alert('Invoice not found for this student.');
      return;
    }

    try {
      // Fetch invoice with student data
      const { data: invoiceData, error: invoiceError } = await supabase
        .from('student_invoices')
        .select(`
          *,
          students:student_id (
            id,
            first_name,
            middle_name,
            last_name,
            student_id_no,
            class_id,
            classes:class_id (id, class_name)
          )
        `)
        .eq('id', invoice.id)
        .single();

      if (invoiceError) throw invoiceError;

      // Fetch invoice items
      const { data: itemsData } = await supabase
        .from('invoice_items')
        .select('*')
        .eq('invoice_id', invoice.id);

      const invoiceWithItems = {
        ...invoiceData,
        invoice_items: itemsData || []
      };

      // Generate PDF using the same function
      const student = invoiceWithItems.students;
      const academicYear = academicYears.find(ay => ay.id === parseInt(selectedAcademicYear));
      const termData = terms.find(t => t.id === parseInt(selectedTerm));

      // Convert invoice_items to the format expected by generateInvoicePDF
      const invoiceItemsForPDF = (invoiceWithItems.invoice_items || []).map(item => ({
        class_fee_mapping: {
          fee_name: item.fee_name,
          fee_category: null,
          amount: item.amount
        },
        fee_name: item.fee_name,
        amount: parseFloat(item.amount || 0),
        discount: parseFloat(item.discount || 0),
        balance: parseFloat(item.balance || 0)
      }));

      generateInvoicePDF(student, invoiceWithItems, invoiceItemsForPDF, academicYear, termData?.id || selectedTerm);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Failed to download invoice. Please try again.');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
      />
      
      <SchoolSidebar schoolData={schoolData} />
      
      <div className="main-content" style={{ 
        width: '100%', 
        maxWidth: '100%', 
        overflowX: 'hidden',
        boxSizing: 'border-box',
        padding: '1.5rem'
      }}>
        <div className="content-header" style={{ 
          width: '100%', 
          maxWidth: '100%',
          boxSizing: 'border-box',
          marginBottom: '1rem'
        }}>
          <h1>Billing</h1>
          <p>Generate invoices for students - Done once a term by admin</p>
        </div>

        {/* Selectors Row */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '1rem', 
          marginBottom: '2rem',
          alignItems: 'flex-end',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          marginTop: '0'
        }}>
          <div className="form-group" style={{ minWidth: '200px', flex: '1' }}>
            <label htmlFor="academic_year">Academic Year *</label>
            <select
              id="academic_year"
              value={selectedAcademicYear}
              onChange={(e) => setSelectedAcademicYear(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
            >
              <option value="">Select Academic Year</option>
              {academicYears.map(year => (
                <option key={year.id} value={year.id}>
                  {year.year_name || `${new Date(year.start_date).getFullYear()}-${new Date(year.end_date).getFullYear()}`}
                </option>
              ))}
            </select>
          </div>

          <BranchSelector
            schoolId={schoolData?.id}
            selectedBranchId={selectedBranchId}
            onBranchChange={handleBranchChange}
          />

          <div className="form-group" style={{ minWidth: '150px', flex: '1' }}>
            <label htmlFor="term">Term *</label>
            <select
              id="term"
              value={selectedTerm}
              onChange={(e) => setSelectedTerm(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
            >
              <option value="">Select Term</option>
              {terms.map(term => (
                <option key={term.id} value={term.id}>{term.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ minWidth: '150px' }}>
            <label>View Mode</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setViewMode('all')}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: viewMode === 'all' ? '#FF8C42' : '#E5E7EB',
                  color: viewMode === 'all' ? 'white' : '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.875rem'
                }}
              >
                All Classes
              </button>
              <button
                onClick={() => setViewMode('class-wise')}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: viewMode === 'class-wise' ? '#FF8C42' : '#E5E7EB',
                  color: viewMode === 'class-wise' ? 'white' : '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '0.875rem'
                }}
              >
                Class-wise
              </button>
            </div>
          </div>

          {viewMode === 'class-wise' && (
            <div className="form-group" style={{ minWidth: '200px', flex: '1' }}>
              <label htmlFor="class">Class</label>
              <select
                id="class"
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}
              >
                <option value="">Select Class</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>
                    {getClassDisplayName(cls.class_name)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Generate Invoice Buttons */}
        {selectedAcademicYear && selectedTerm && students.length > 0 && (
          <div style={{ 
            display: 'flex', 
            gap: '1rem', 
            marginBottom: '1.5rem',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => handleGenerateInvoice('all')}
              className="btn-primary"
              style={{ padding: '0.75rem 1.5rem' }}
            >
              Generate Invoices (All Classes - Bulk)
            </button>
            {viewMode === 'class-wise' && selectedClass && (
              <button
                onClick={() => handleGenerateInvoice('class')}
                className="btn-primary"
                style={{ padding: '0.75rem 1.5rem' }}
              >
                Generate Invoices (Class-wise)
              </button>
            )}
          </div>
        )}

        {/* Billing Table */}
        <div className="dashboard-section" style={{ 
          width: '100%', 
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}>
          <h2>Billing Table</h2>
          
          {loadingData ? (
            <p>Loading billing data...</p>
          ) : !selectedAcademicYear || !selectedTerm ? (
            <p style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
              Please select Academic Year and Term to view billing data.
            </p>
          ) : students.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
              No students found for the selected criteria.
            </p>
          ) : (
            <div className="table-container" style={{
              width: '100%',
              maxWidth: '100%',
              overflowX: 'auto',
              overflowY: 'visible',
              boxSizing: 'border-box',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'thin',
              scrollbarColor: '#FF8C42 #f1f1f1',
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e9ecef',
              margin: '0',
              padding: '0'
            }}>
              <style>{`
                .table-container::-webkit-scrollbar {
                  height: 12px;
                }
                .table-container::-webkit-scrollbar-track {
                  background: #f1f1f1;
                  border-radius: 6px;
                }
                .table-container::-webkit-scrollbar-thumb {
                  background: #FF8C42;
                  border-radius: 6px;
                }
                .table-container::-webkit-scrollbar-thumb:hover {
                  background: #E67A35;
                }
              `}</style>
              <table className="data-table" style={{
                width: '100%',
                minWidth: '1000px',
                borderCollapse: 'collapse',
                fontSize: '0.9rem',
                tableLayout: 'auto',
                margin: '0'
              }}>
                <thead>
                  <tr>
                    <th>Student Name</th>
                    <th>Admission No</th>
                    <th>Class</th>
                    <th>Fee Name</th>
                    <th>Term</th>
                    <th>Amount</th>
                    <th>Discount</th>
                    <th>Balance</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => {
                    const studentBills = getStudentBillingData(student.id);
                    const totals = calculateStudentTotals(student.id);
                    const hasBills = studentBills.length > 0;

                    return (
                      <React.Fragment key={student.id}>
                        {hasBills ? (
                          studentBills.map((bill, index) => (
                            <tr key={`${student.id}-${bill.id}-${index}`}>
                              {index === 0 && (
                                <>
                                  <td rowSpan={studentBills.length} style={{ verticalAlign: 'top', fontWeight: '600' }}>
                                    {`${student.first_name || ''} ${student.middle_name || ''} ${student.last_name || ''}`.trim()}
                                  </td>
                                  <td rowSpan={studentBills.length} style={{ verticalAlign: 'top' }}>
                                    {student.student_id_no || 'N/A'}
                                  </td>
                                  <td rowSpan={studentBills.length} style={{ verticalAlign: 'top' }}>
                                    {student.classes?.class_name ? getClassDisplayName(student.classes.class_name) : 'N/A'}
                                  </td>
                                </>
                              )}
                              <td>{bill.class_fee_mapping?.fee_name || bill.fee_name || 'N/A'}</td>
                              <td>{bill.term || selectedTerm}</td>
                              <td>₨{parseFloat(bill.amount || 0).toFixed(2)}</td>
                              <td>₨{parseFloat(bill.discount || 0).toFixed(2)}</td>
                              <td>₨{(parseFloat(bill.amount || 0) - parseFloat(bill.discount || 0)).toFixed(2)}</td>
                              {index === 0 && (
                                <td rowSpan={studentBills.length} style={{ verticalAlign: 'top' }}>
                                  {studentInvoices[student.id] ? (
                                    <button
                                      onClick={() => downloadInvoicePDF(student.id)}
                                      className="btn-primary"
                                      style={{ 
                                        padding: '0.5rem 1rem', 
                                        fontSize: '0.875rem',
                                        background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        transition: 'all 0.3s ease',
                                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.transform = 'translateY(-2px)';
                                        e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                                      }}
                                    >
                                      📥 Download Invoice
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleGenerateInvoice('single', student.id)}
                                      className="btn-primary"
                                      style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                                    >
                                      Generate Invoice
                                    </button>
                                  )}
                                </td>
                              )}
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td>
                              {`${student.first_name || ''} ${student.middle_name || ''} ${student.last_name || ''}`.trim()}
                            </td>
                            <td>{student.student_id_no || 'N/A'}</td>
                            <td>
                              {student.classes?.class_name ? getClassDisplayName(student.classes.class_name) : 'N/A'}
                            </td>
                            <td colSpan="5" style={{ textAlign: 'center', color: '#6B7280' }}>
                              No billing data available
                            </td>
                            <td>
                              {studentInvoices[student.id] ? (
                                <button
                                  onClick={() => downloadInvoicePDF(student.id)}
                                  className="btn-primary"
                                  style={{ 
                                    padding: '0.5rem 1rem', 
                                    fontSize: '0.875rem',
                                    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                                  }}
                                >
                                  📥 Download Invoice
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleGenerateInvoice('single', student.id)}
                                  className="btn-primary"
                                  style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                                  disabled
                                >
                                  Generate Invoice
                                </button>
                              )}
                            </td>
                          </tr>
                        )}
                        {hasBills && (
                          <tr style={{ backgroundColor: '#F9FAFB', fontWeight: '600' }}>
                            <td colSpan="5" style={{ textAlign: 'right', paddingRight: '1rem' }}>
                              Total:
                            </td>
                            <td>₨{totals.totalAmount.toFixed(2)}</td>
                            <td>₨{totals.totalDiscount.toFixed(2)}</td>
                            <td>₨{totals.totalBalance.toFixed(2)}</td>
                            <td></td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Invoice Generation Confirmation Popup */}
      {showInvoicePopup && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            backdropFilter: 'blur(6px)'
          }}
          onClick={() => setShowInvoicePopup(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '20px',
              padding: '3rem 2.5rem',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4)',
              textAlign: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{
              margin: '0 0 1rem 0',
              color: '#1F2937',
              fontSize: '1.5rem',
              fontWeight: '700'
            }}>
              Generate Invoices
            </h2>
            <p style={{
              margin: '0 0 2rem 0',
              color: '#4B5563',
              fontSize: '1rem',
              lineHeight: '1.6'
            }}>
              {invoiceMode === 'all' && `Generate invoices for all ${selectedStudents.length} students?`}
              {invoiceMode === 'class' && `Generate invoices for ${selectedStudents.length} students in the selected class?`}
              {invoiceMode === 'single' && `Generate invoice for this student?`}
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button
                onClick={() => setShowInvoicePopup(false)}
                style={{ 
                  padding: '0.75rem 2rem',
                  backgroundColor: '#E5E7EB',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  minWidth: '120px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#D1D5DB';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#E5E7EB';
                  e.target.style.transform = 'translateY(0)';
                }}
                disabled={generatingInvoice}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmInvoiceGeneration}
                style={{ 
                  padding: '0.75rem 2rem',
                  background: 'linear-gradient(135deg, #FF8C42 0%, #E67E22 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 6px 20px rgba(255, 139, 61, 0.4)',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  minWidth: '120px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px) scale(1.02)';
                  e.target.style.boxShadow = '0 10px 30px rgba(255, 139, 61, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0) scale(1)';
                  e.target.style.boxShadow = '0 6px 20px rgba(255, 139, 61, 0.4)';
                }}
                disabled={generatingInvoice}
              >
                {generatingInvoice ? 'Generating...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Beautiful Invoice Success Popup */}
      {showInvoiceSuccessPopup && invoiceSuccessData && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in',
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => {
            setShowInvoiceSuccessPopup(false);
            setInvoiceSuccessData(null);
          }}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Success Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              margin: '0 auto 24px',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out 0.2s both'
            }}>
              <svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="50" cy="50" r="50" fill="#10B981" opacity="0.1"/>
                <circle cx="50" cy="50" r="40" fill="#10B981" opacity="0.2"/>
                <path 
                  d="M30 50 L45 65 L70 35" 
                  stroke="#10B981" 
                  strokeWidth="5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  fill="none"
                  style={{
                    animation: 'checkmark 0.6s ease-out 0.4s both',
                    strokeDasharray: '50',
                    strokeDashoffset: '50'
                  }}
                />
              </svg>
            </div>

            {/* Title */}
            <h2 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: '#10B981',
              margin: '0 0 16px 0',
              position: 'relative',
              zIndex: 1
            }}>
              Invoice Generated Successfully!
            </h2>

            {/* Message */}
            <p style={{
              fontSize: '16px',
              color: '#6B7280',
              margin: '0 0 24px 0',
              lineHeight: '1.6',
              position: 'relative',
              zIndex: 1
            }}>
              {invoiceSuccessData.count === 1 
                ? `Invoice has been generated and PDF downloaded for ${invoiceSuccessData.invoices[0]?.studentName || 'the student'}.`
                : `Successfully generated ${invoiceSuccessData.count} invoices and downloaded PDFs.`
              }
            </p>

            {/* Invoice Details */}
            {invoiceSuccessData.invoices && invoiceSuccessData.invoices.length > 0 && (
              <div style={{
                background: '#F9FAFB',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px',
                textAlign: 'left',
                position: 'relative',
                zIndex: 1,
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {invoiceSuccessData.invoices.map((inv, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: index < invoiceSuccessData.invoices.length - 1 ? '1px solid #E5E7EB' : 'none'
                  }}>
                    <div>
                      <div style={{ fontWeight: '600', color: '#1F2937', fontSize: '14px' }}>
                        {inv.studentName}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                        {inv.invoiceNumber}
                      </div>
                    </div>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#10B981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M11.6667 3.5L5.25 9.91667L2.33334 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Close Button */}
            <button
              onClick={() => {
                setShowInvoiceSuccessPopup(false);
                setInvoiceSuccessData(null);
              }}
              style={{
                position: 'relative',
                zIndex: 1,
                padding: '0.75rem 2rem',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '25px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 4px 15px rgba(16, 185, 129, 0.4)';
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(30px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes scaleIn {
          from {
            transform: scale(0);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes checkmark {
          0% {
            stroke-dashoffset: 50;
          }
          100% {
            stroke-dashoffset: 0;
          }
        }
      `}</style>
    </div>
  );
}

export default Billing;
